/**
 * Shared lobby credentials card — used by lobby-code-input (submitted state)
 * and lobby-created. Displays labeled fields in a unified access-card layout
 * with a header strip and divided rows.
 */

import { Key } from "lucide-react";
import { SecretField } from "./SecretField";

export type LobbyCredentialField = {
  key: string;
  label: string;
  value: string;
  type: "text" | "password";
};

type LobbyCredentialsCardProps = {
  fields: LobbyCredentialField[];
};

export function LobbyCredentialsCard({ fields }: LobbyCredentialsCardProps) {
  return (
    <div className="relative w-full max-w-sm rounded-lg bg-surface-800 border border-surface-700/50 overflow-hidden">
      {/* Header strip */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-surface-600/40 bg-surface-900/50">
        <Key size={12} className="text-brand-400/70" aria-hidden="true" />
        <span className="text-[10px] font-display font-semibold uppercase tracking-[0.2em] text-surface-400">
          Lobby Credentials
        </span>
      </div>

      {/* Field rows */}
      <div className="divide-y divide-surface-600/40">
        {fields.map((field) => (
          <div key={field.key} className="flex items-center justify-between gap-4 px-4 py-3">
            <span className="text-[11px] text-surface-500 uppercase tracking-wider font-medium">
              {field.label}
            </span>
            {field.type === "password" ? (
              <SecretField value={field.value} />
            ) : (
              <span className="font-mono font-bold text-brand-400 text-base tracking-[0.15em] tabular-nums">
                {field.value || "—"}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
